import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';

export function createCatalogoRepository({ getFirebase }) {
  const firstPageCache = new Map();
  const productoCache = new Map();

  function keyAuth(isAuthenticated) {
    return isAuthenticated ? 'auth' : 'guest';
  }

  function mapDocs(docs) {
    return docs.map((d) => {
      const item = { id: d.id, ...d.data() };
      productoCache.set(item.id, item);
      return item;
    });
  }

  return {
    invalidateCatalogoCache() {
      firstPageCache.clear();
      productoCache.clear();
    },
    async listarProductosParaCatalogoPage({
      isAuthenticated,
      pageSize = 24,
      /** @type {import('firebase/firestore').QueryDocumentSnapshot | null} */
      cursor = null,
    }) {
      const { db } = getFirebase();
      const authKey = keyAuth(Boolean(isAuthenticated));
      if (!cursor && firstPageCache.has(authKey)) {
        return firstPageCache.get(authKey);
      }

      const parts = [
        collection(db, 'productos'),
        where('estado', '==', 'publicado'),
        orderBy(documentId()),
        ...(cursor ? [startAfter(cursor)] : []),
        limit(pageSize),
      ];
      const q = query(...parts);
      const snap = await getDocs(q);
      const items = mapDocs(snap.docs);
      const lastDoc = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
      const result = {
        items,
        nextCursor: lastDoc,
        hasMore: snap.size >= pageSize,
      };
      if (!cursor) firstPageCache.set(authKey, result);
      return result;
    },

    /** Catálogo público: publicaciones activas de un vendedor por UID. */
    async listarProductosPublicadosPorVendedorUid(vendedorUid, max = 48) {
      const uid = String(vendedorUid || '').trim();
      if (!uid) return [];
      const { db } = getFirebase();
      const lim = Math.min(60, Math.max(1, Number(max) || 48));
      const q = query(
        collection(db, 'productos'),
        where('vendedor_uid', '==', uid),
        where('estado', '==', 'publicado'),
        orderBy(documentId()),
        limit(lim)
      );
      const snap = await getDocs(q);
      return mapDocs(snap.docs);
    },

    async listarProductosVendidosVitrinaVendedor(vendedorUid, max = 40) {
      const { db } = getFirebase();
      const uid = String(vendedorUid || '').trim();
      if (!uid) return [];
      const q = query(
        collection(db, 'productos'),
        where('vendedor_uid', '==', uid),
        where('estado', '==', 'vendido'),
        orderBy('vendido_en', 'desc'),
        limit(Math.min(60, Math.max(1, Number(max) || 40)))
      );
      const snap = await getDocs(q);
      return mapDocs(snap.docs).filter(
        (it) => it.listado_catalogo_vendido === true
      );
    },

    async obtenerProductoPorId(id, opts = {}) {
      const key = String(id);
      const forceServer = Boolean(opts.forceServer);
      if (!forceServer && productoCache.has(key)) return productoCache.get(key);
      const { db } = getFirebase();
      const ref = doc(db, 'productos', key);
      const snap = forceServer ? await getDocFromServer(ref) : await getDoc(ref);
      if (!snap.exists()) {
        productoCache.delete(key);
        return null;
      }
      const item = { id: snap.id, ...snap.data() };
      productoCache.set(item.id, item);
      return item;
    },

    /**
     * Destacados con `producto_id`: solo si el producto sigue `publicado` (evita promo con ventas ya marcadas).
     * Filas solo-DNI se mantienen.
     */
    async filtrarDestacadosProductoAunPublicado(rows) {
      if (!Array.isArray(rows) || !rows.length) return rows || [];
      const out = [];
      for (const row of rows) {
        const pid = String(row?.producto_id || '').trim();
        if (!pid) {
          out.push(row);
          continue;
        }
        try {
          const p = await this.obtenerProductoPorId(pid, { forceServer: true });
          if (p && String(p.estado || '') === 'publicado') out.push(row);
        } catch (_e) {
          /* sin red: no mostramos slot con producto dudoso */
        }
      }
      return out;
    },

    async listarRelacionados({ excludeId, categoriaId, isAuthenticated, max = 2 }) {
      const { db } = getFirebase();
      const seen = new Set([String(excludeId || '')]);
      const cat = categoriaId || 'general';

      const parts = [collection(db, 'productos')];
      if (!isAuthenticated) parts.push(where('estado', '==', 'publicado'));
      parts.push(limit(30));
      const snap = await getDocs(query(...parts));

      const candidatos = [];
      for (const d of snap.docs) {
        if (seen.has(d.id)) continue;
        const data = d.data();
        const item = { id: d.id, ...data };
        const mismaCat = (data.categoria_id || 'general') === cat;
        candidatos.push({ item, mismaCat });
      }
      candidatos.sort((a, b) => Number(b.mismaCat) - Number(a.mismaCat));

      const out = [];
      for (const { item } of candidatos) {
        if (out.length >= max) break;
        seen.add(item.id);
        productoCache.set(item.id, item);
        out.push(item);
      }
      return out;
    },

    /** Para detalle de producto: publicados, prioridad negocio premium y misma categoría; sin scroll infinito. */
    async listarMasProductosParaDetalle({ excludeId, categoriaId, max = 12 }) {
      const { db } = getFirebase();
      const ex = String(excludeId || '');
      const cat = categoriaId || 'general';
      const cap = Math.min(24, Math.max(4, Number(max) || 12));
      const q = query(
        collection(db, 'productos'),
        where('estado', '==', 'publicado'),
        orderBy(documentId()),
        limit(100)
      );
      const snap = await getDocs(q);
      let items = mapDocs(snap.docs).filter((it) => it.id !== ex);
      items.sort((a, b) => {
        const pa = a.negocio_premium === true ? 1 : 0;
        const pb = b.negocio_premium === true ? 1 : 0;
        if (pb !== pa) return pb - pa;
        const ca = (a.categoria_id || 'general') === cat ? 1 : 0;
        const cb = (b.categoria_id || 'general') === cat ? 1 : 0;
        if (cb !== ca) return cb - ca;
        return 0;
      });
      items = items.slice(0, cap);
      for (const it of items) productoCache.set(it.id, it);
      return items;
    },

    /**
     * Siguientes publicados de la misma categoría (orden por id de documento).
     * Requiere índice compuesto en Firestore: estado + categoria_id + __name__.
     */
    async listarProductosFeedCategoriaSiguientes({
      categoriaId,
      afterProductId,
      pageSize = 8,
    }) {
      const { db } = getFirebase();
      const cat = categoriaId || 'general';
      const ref = doc(db, 'productos', String(afterProductId));
      const afterSnap = await getDoc(ref);
      if (!afterSnap.exists()) {
        return { items: [], hasMore: false };
      }
      const q = query(
        collection(db, 'productos'),
        where('estado', '==', 'publicado'),
        where('categoria_id', '==', cat),
        orderBy(documentId()),
        startAfter(afterSnap),
        limit(pageSize)
      );
      const snap = await getDocs(q);
      const items = mapDocs(snap.docs);
      return {
        items,
        hasMore: snap.size >= pageSize,
      };
    },
  };
}
